import { useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { LogOut, LayoutDashboard, FilePlus2, Inbox, Shield, Calculator, TrendingUp, Users, Calendar, PanelLeft, Menu, Settings as SettingsIcon, CalendarDays, HelpCircle, Sparkles, Mail, Map, BookOpen, Layers, FileSignature, ChevronRight, Banknote, Handshake, Trees, Home, Landmark, HandCoins } from "lucide-react";
import InstallPrompt from "@/components/InstallPrompt";
import DiscordIcon from "@/components/DiscordIcon";
import NotificationsBell from "@/components/NotificationsBell";

function useSidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("wu_sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem("wu_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  return { collapsed, setCollapsed, mobileOpen, setMobileOpen };
}

function NavItem({ to, label, Icon, active, badge, collapsed, onClick, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onClick}
      className={`relative flex items-center gap-3 my-0.5 px-3 py-2.5 rounded-2xl text-sm transition ${collapsed ? "justify-center" : ""}`}
      style={{
        color: active ? "var(--lime)" : "var(--text)",
        background: active ? "var(--lime-glow)" : "transparent",
        fontWeight: active ? 600 : 500,
      }}
      title={collapsed ? label : undefined}
    >
      <Icon size={17} strokeWidth={1.75} className="flex-shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {badge > 0 && (
        <span
          data-testid="nav-unread-badge"
          className={`inline-flex items-center justify-center font-mono text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] ${collapsed ? "absolute -top-0.5 -right-0.5" : ""}`}
          style={{
            background: "var(--lime)",
            color: "#0c0c0c",
            boxShadow: "0 0 8px rgba(197,226,106,0.6)",
          }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

function NavGroup({ label, Icon, children, active, childActive, collapsed, onNavigate, onExpandSidebar, testid }) {
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);

  const toggle = () => {
    if (collapsed) {
      onExpandSidebar?.();
      setOpen(true);
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <div>
      <button
        onClick={toggle}
        className={`relative flex items-center gap-3 w-full my-0.5 rounded-2xl text-sm transition hover:bg-white/5 ${collapsed ? "justify-center px-3 py-2.5" : "pl-3 pr-2 py-2.5"}`}
        style={{
          color: active || childActive ? "var(--lime)" : "var(--text)",
          background: active || childActive ? "var(--lime-glow)" : "transparent",
          fontWeight: active || childActive ? 600 : 500,
        }}
        title={collapsed ? label : undefined}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <Icon size={17} strokeWidth={1.75} className="flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left" data-testid={testid}>{label}</span>
            <ChevronRight size={14} className="transition-transform flex-shrink-0" style={{ transform: open ? "rotate(90deg)" : "none" }} />
          </>
        )}
      </button>
      {open && !collapsed && (
        <div className="ml-5 pl-3 mb-1" style={{ borderLeft: "1px solid var(--border)" }} data-testid={`${testid}-children`}>
          {children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={onNavigate}
              className="flex items-center gap-2.5 my-0.5 px-2 py-2 rounded-xl text-[13px] transition hover:bg-white/5"
              style={{ color: c.isActive ? "var(--lime)" : "var(--text-muted)", fontWeight: c.isActive ? 600 : 500 }}
              data-testid={`${testid}-child-${c.id}`}
            >
              <c.icon size={14} strokeWidth={1.75} className="flex-shrink-0" />
              <span className="truncate">{c.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const sb = useSidebar();
  const [unread, setUnread] = useState(0);
  const [followupsDue, setFollowupsDue] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const poll = async () => {
      try {
        const { data } = user.is_admin
          ? await api.get("/admin/stats")
          : await api.get("/deals/unread-count");
        setUnread(data.unread || 0);
      } catch (e) { /* ignore */ }
      if (!user.is_admin) {
        try {
          const { data } = await api.get("/leads/followups-due");
          setFollowupsDue(data.count || 0);
        } catch (e) { /* ignore */ }
      }
    };
    poll();
    pollRef.current = setInterval(poll, 30000);
    return () => clearInterval(pollRef.current);
  }, [user, loc.pathname]);

  const navLinks = user?.is_admin
    ? [
        { to: "/admin", label: "Admin", icon: Shield, badge: unread },
        { to: "/journey", label: "The Journey", icon: Map },
        { to: "/admin/journey", label: "Manage Journey", icon: Layers },
        { to: "/admin/kpis", label: "Student KPIs", icon: TrendingUp },
        { to: "/admin/office-hours", label: "Student Calls", icon: Calendar },
        { to: "/admin/support", label: "Support Inbox", icon: HelpCircle },
        { to: "/admin/emails", label: "Email Previews", icon: Mail },
        { to: "/resources", label: "Resources", icon: BookOpen },
        { to: "/admin/resources", label: "Manage Resources", icon: Layers },
        { to: "/admin/ai-coach", label: "AI Rulebook", icon: Sparkles },
      ]
    : [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/leads", label: "Leads", icon: Users, badge: followupsDue },
        { to: "/submit", label: "Submit Deal", icon: FilePlus2 },
        {
          to: "/calculator",
          label: "Calculators",
          icon: Calculator,
          children: [
            { id: "cash", to: "/calculator?tab=cash", label: "Cash", icon: Banknote },
            { id: "creative", to: "/calculator?tab=creative", label: "Creative Finance", icon: Handshake },
            { id: "land", to: "/calculator?tab=land", label: "Vacant Land", icon: Trees },
            { id: "zillow", to: "/calculator?tab=zillow", label: "Zillow", icon: Home },
          ],
        },
        {
          to: "/contracts",
          label: "Create Contract",
          icon: FileSignature,
          children: [
            { id: "house", to: "/contracts?type=house", label: "House", icon: Home },
            { id: "land", to: "/contracts?type=land", label: "Land", icon: Trees },
            { id: "subto", to: "/contracts?type=subto", label: "Mortgage Takeover", icon: Landmark },
            { id: "seller_finance", to: "/contracts?type=seller_finance", label: "Seller Finance", icon: HandCoins },
          ],
        },
        { to: "/calendar", label: "Calendar", icon: CalendarDays },
        { to: "/book", label: "Book Call", icon: Calendar },
        { to: "/kpis", label: "My KPIs", icon: TrendingUp },
        { to: "/deals", label: "My Deals", icon: Inbox, badge: unread },
        { to: "/coach", label: "AI Coach", icon: Sparkles },
        { to: "/resources", label: "Resources", icon: BookOpen },
        { to: "/support", label: "Support", icon: HelpCircle },
      ];

  const isActive = (to) => (to === "/admin" ? loc.pathname === "/admin" : loc.pathname.startsWith(to));
  const sidebarWidth = sb.collapsed ? 76 : 240;

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Mobile overlay */}
      {sb.mobileOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => sb.setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`wu-sidebar fixed top-0 left-0 h-screen z-50 flex flex-col transition-all duration-200 ${sb.mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        style={{
          width: sidebarWidth,
          background: "var(--sidebar-bg)",
          borderRight: "1px solid var(--border)",
        }}
        data-testid="sidebar"
      >
        {/* Brand block */}
        <div className="px-3 pt-5 pb-4 flex flex-col items-center">
          <button
            onClick={() => { navigate("/dashboard"); sb.setMobileOpen(false); }}
            className="flex items-center justify-center w-full"
            data-testid="brand-home-button"
          >
            <img
              src="/wu_logo_full.png"
              alt="Wholesalers University"
              className={`wu-brand-logo ${sb.collapsed ? "h-8 w-auto" : "h-16 w-auto"}`}
            />
          </button>
        </div>

        {/* Collapse toggle (desktop only), centered to line up with the nav icons */}
        <div className="hidden md:flex px-3 pb-3 justify-center border-b" style={{ borderColor: "var(--border)" }}>
          <button
            onClick={() => sb.setCollapsed((v) => !v)}
            className="p-2 rounded-xl transition hover:bg-white/5"
            style={{ color: "var(--text-muted)" }}
            data-testid="sidebar-toggle"
            title={sb.collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <PanelLeft size={16} />
          </button>
        </div>

        {/* Section label */}
        {!sb.collapsed && (
          <div className="px-4 pt-4 pb-1">
            <span
              className="text-[11px] uppercase tracking-wider font-medium"
              style={{ color: "var(--text-dim)" }}
            >
              Navigation
            </span>
          </div>
        )}

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-2 pb-2" data-testid="sidebar-nav">
          {navLinks.map((l) => {
            const testid = `nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
            if (l.children) {
              const onPage = loc.pathname.startsWith(l.to);
              const key = l.to === "/calculator" ? "tab" : "type";
              const current = new URLSearchParams(loc.search).get(key) || l.children[0].id;
              return (
                <NavGroup
                  key={l.to}
                  label={l.label}
                  Icon={l.icon}
                  active={onPage}
                  childActive={onPage}
                  collapsed={sb.collapsed}
                  onNavigate={() => sb.setMobileOpen(false)}
                  onExpandSidebar={() => sb.setCollapsed(false)}
                  testid={testid}
                  children={l.children.map((c) => ({ ...c, isActive: onPage && current === c.id }))}
                />
              );
            }
            return (
              <NavItem
                key={l.to}
                to={l.to}
                label={l.label}
                Icon={l.icon}
                active={isActive(l.to)}
                badge={l.badge}
                collapsed={sb.collapsed}
                onClick={() => sb.setMobileOpen(false)}
                testid={testid}
              />
            );
          })}
        </nav>

        {/* Bottom: Settings + Sign Out */}
        <div className="border-t px-2 py-3" style={{ borderColor: "var(--border)" }}>
          <NavItem
            to="/settings"
            label="Settings"
            Icon={SettingsIcon}
            active={isActive("/settings")}
            collapsed={sb.collapsed}
            onClick={() => sb.setMobileOpen(false)}
            testid="nav-settings"
          />
          <button
            onClick={logout}
            className={`relative flex items-center gap-3 w-full my-0.5 px-3 py-2.5 rounded-2xl text-sm transition hover:bg-white/5 ${sb.collapsed ? "justify-center" : ""}`}
            style={{ color: "var(--text)", fontWeight: 500 }}
            data-testid="sidebar-logout-button"
            title={sb.collapsed ? "Sign Out" : undefined}
          >
            <LogOut size={17} strokeWidth={1.75} className="flex-shrink-0" />
            {!sb.collapsed && <span className="flex-1 truncate text-left">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Right side: header + content */}
      <div
        className="flex-1 flex flex-col min-w-0 transition-all duration-200 app-content"
        style={{ "--sb-w": `${sidebarWidth}px` }}
      >
        <header
          className="sticky top-0 z-30 backdrop-blur-xl flex items-center justify-between px-4 md:px-8 h-16"
          style={{
            background: "var(--header-bg)",
            borderBottom: "1px solid var(--border)",
            paddingTop: "env(safe-area-inset-top, 0px)",
            minHeight: "calc(4rem + env(safe-area-inset-top, 0px))",
          }}
        >
          {/* Mobile menu */}
          <button
            onClick={() => sb.setMobileOpen(true)}
            className="md:hidden p-2 rounded transition hover:bg-white/5"
            style={{ color: "var(--text-muted)" }}
            data-testid="mobile-menu-button"
          >
            <Menu size={18} />
          </button>
          <div className="flex-1 md:flex-none" />
          <div className="flex items-center gap-2 md:gap-3">
            {!user?.is_admin && (
              <Link
                to="/profile"
                className="hidden sm:inline-block font-mono text-xs transition hover:opacity-80"
                style={{ color: loc.pathname === "/profile" ? "var(--lime)" : "var(--text-muted)" }}
                data-testid="user-email"
              >
                {user?.name || user?.email}
              </Link>
            )}
            {user?.is_admin && (
              <span className="hidden sm:inline-block font-mono text-xs" style={{ color: "var(--text-muted)" }} data-testid="user-email">
                {user?.name || user?.email}
              </span>
            )}
            <NotificationsBell />
          </div>
        </header>

        <main className="flex-1">
          <div
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 fade-up"
            style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}
          >{children}</div>
        </main>
        <footer className="hidden md:block py-8 text-center font-mono text-xs" style={{ color: "var(--text-dim)" }}>
          © Wholesalers University &middot; Student Portal
        </footer>
      </div>

      <InstallPrompt />

      {user && !user.is_admin && (
        <nav
          className="md:hidden mobile-bottom-nav"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "var(--sidebar-bg)",
            borderTop: "1px solid var(--border)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
            boxShadow: "0 -4px 16px rgba(0,0,0,0.5)",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            willChange: "transform",
            isolation: "isolate",
          }}
          data-testid="mobile-bottom-nav"
        >
          <div className="grid grid-cols-5">
            {[
              { to: "/dashboard", label: "Home", icon: LayoutDashboard },
              { to: "/leads", label: "Leads", icon: Users, badge: followupsDue },
              { to: "/submit", label: "Submit", icon: FilePlus2 },
              { to: "/calendar", label: "Calendar", icon: CalendarDays },
              { to: "/coach", label: "Coach", icon: Sparkles },
            ].map((item) => {
              const Active = isActive(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="mobile-nav-link flex flex-col items-center justify-center gap-0.5 py-2 transition relative"
                  style={{ color: Active ? "var(--lime)" : "var(--text-muted)" }}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {Active && (
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[3px] rounded-b"
                      style={{ background: "var(--lime)", boxShadow: "0 0 8px var(--lime)" }}
                    />
                  )}
                  <div className="relative">
                    <Icon size={20} strokeWidth={1.75} />
                    {item.badge > 0 && (
                      <span
                        className="absolute -top-1 -right-2 font-mono text-[9px] rounded-full px-1 min-w-[14px] h-[14px] flex items-center justify-center"
                        style={{ background: "var(--lime)", color: "#0c0c0c", lineHeight: 1 }}
                      >
                        {item.badge > 9 ? "9+" : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
